import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Canonical org settings type. Single source of truth for all frontend consumers.
 * Only admin/Settings.tsx fetches the full row independently (editable form).
 */
export interface OrgSettings {
  id: string;
  name: string;
  timezone: string;
  currency: string;
  rate_decimal_places: number;
  default_language: string;
  application_cutoff_day_of_week: number;
  application_cutoff_time: string;
  listing_publish_day_of_week: number;
  listing_publish_time: string;
  offer_response_days: number;
  map_enabled: boolean;
  map_object_path: string | null;
  can_manage_resource_types: boolean;
  phone_default_region: string | null;
  notify_new_offers: boolean;
  notify_new_listings: boolean;
  notify_offer_expiry: boolean;
}

const ORG_SETTINGS_FIELDS = `
  id, name, timezone, currency, rate_decimal_places, default_language,
  application_cutoff_day_of_week, application_cutoff_time,
  listing_publish_day_of_week, listing_publish_time,
  offer_response_days, map_enabled, map_object_path,
  can_manage_resource_types, phone_default_region,
  notify_new_offers, notify_new_listings, notify_offer_expiry
` as const;

/**
 * Centralized hook for org settings. Fetches once per active org session via React Query.
 *
 * Fail-closed behavior: when currentOrgId is falsy (account-only routes, no-org states),
 * returns { orgSettings: null, isLoading: false, error: null } without throwing or
 * triggering error toasts.
 *
 * Cache key: ['org-settings', currentOrgId]
 * Invalidate after settings save: queryClient.invalidateQueries({ queryKey: ['org-settings', currentOrgId] })
 */
export function useOrgSettings() {
  const { currentOrgId } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['org-settings', currentOrgId],
    queryFn: async (): Promise<OrgSettings | null> => {
      const { data, error } = await supabase
        .from('orgs')
        .select(ORG_SETTINGS_FIELDS)
        .eq('id', currentOrgId!)
        .maybeSingle();

      // Normalize "no row" into null (valid state), only surface genuine query errors.
      if (error) {
        // PostgREST can return "no rows" as an error in some modes; treat it as null-state.
        const code = (error as any)?.code;
        const details = String((error as any)?.details ?? '');
        const message = String((error as any)?.message ?? '');

        const isNoRow =
          code === 'PGRST116' || // "Results contain 0 rows" (common for .single())
          details.toLowerCase().includes('0 rows') ||
          message.toLowerCase().includes('0 rows');

        if (!isNoRow) throw error;
        return null;
      }

      return (data as OrgSettings | null) ?? null;
    },
    enabled: !!currentOrgId,
    staleTime: 30_000,
  });

  return {
    orgSettings: data ?? null,
    isLoading: !!currentOrgId && isLoading,
    error: error ?? null,
    refetch,
  };
}
